import { PayeeEngagementStatus, PayeeRow } from "@wingspanhq/wingspan-api-sdk";

import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";

export type WorkLogEngagementOption = {
  /** `payeeEngagementId` — the only engagement id the work-log endpoints take. */
  value: string;
  /** "Contractor • Assignment". */
  label: string;
  /**
   * `PayeeEngagementType` as a raw string. Only `Employee` engagements get a pay
   * period — see `ModalCreateWorkLogPayer`.
   *
   * Filled in from the engagement template by
   * `selectorWorkLogPayeeEngagementOptions`. The search row no longer carries a
   * type, so the row-based selector below leaves it unset (WIN-16077).
   */
  engagementType?: string | null;
  status?: PayeeEngagementStatus;
};

/**
 * The payer's work-log-capable assignments.
 *
 * `payeeEngagementId` is the only engagement identifier the v3 work-log
 * endpoints accept — `listWorkLogs`'s filter and `WorkLogCreate` both key on it
 * — so that is the option value. A payee row can hold several engagements, and
 * only the ones with work logging enabled can carry a work log.
 *
 * Status and type are carried through rather than filtered here: the table's
 * Engagement filter must still find work logs on since-terminated assignments,
 * while the create modal can only offer live ones. Each caller applies its own
 * policy.
 */
export const selectorWorkLogEngagementOptions = (
  payeeRows: PayeeRow[]
): WorkLogEngagementOption[] =>
  payeeRows.flatMap(payee =>
    (payee.engagements ?? [])
      .filter(engagement => engagement.isWorkLoggingEnabled && !!engagement.id)
      .map(engagement => ({
        value: engagement.id as string,
        label: [selectorSearchRowName(payee), engagement.name]
          .filter(Boolean)
          .join(" • "),
        // `engagementType` is left unset (WIN-16077): SDK 4.2.0 dropped it from
        // the search row, and this selector's only caller (the Engagement
        // filter) does not read it. `selectorWorkLogPayeeEngagementOptions`
        // still fills it in from the engagement template for callers that do.
        status: engagement.status
      }))
  );
